import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useScrollTrigger,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link, useLocation } from 'react-router-dom';

const navItems = [
  { label: 'Features', path: '/features' },
  { label: 'Pricing', path: '/pricing' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });

  useEffect(() => {
    setScrolled(trigger);
  }, [trigger]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Determine if we're on a page with hero (landing) where navbar starts transparent
  const isLandingPage = location.pathname === '/';
  const showSolidBg = scrolled || !isLandingPage;

  const drawer = (
    <Box sx={{ p: 2, height: '100%', bgcolor: 'white' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="img"
            src="/logo192.png"
            alt="SimplyPayroll"
            sx={{
              width: 36,
              height: 36,
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
            SimplyPayroll
          </Typography>
        </Box>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={handleDrawerToggle}
              sx={{
                borderRadius: 2,
                mb: 1,
                color:
                  location.pathname === item.path
                    ? 'primary.main'
                    : 'text.primary',
              }}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              handleDrawerToggle();
              window.location.href = `${
                process.env.REACT_APP_APP_URL || 'https://localhost:6001'
              }/login`;
            }}
            sx={{ borderRadius: 2, mb: 1 }}
          >
            <ListItemText primary="Sign In" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding sx={{ mt: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              handleDrawerToggle();
              window.location.href = `${
                process.env.REACT_APP_APP_URL || 'https://localhost:6001'
              }/register`;
            }}
            sx={{ py: 1.5 }}
          >
            Get Started Free
          </Button>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          bgcolor: showSolidBg ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
          backdropFilter: showSolidBg ? 'blur(10px)' : 'none',
          transition: 'all 0.3s ease',
          boxShadow: showSolidBg ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: 1 }}>
            {/* Logo */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                flexGrow: { xs: 1, md: 0 },
              }}
            >
              <Box
                component="img"
                src="/logo192.png"
                alt="SimplyPayroll"
                sx={{
                  width: 40,
                  height: 40,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: showSolidBg ? '#1a1a1a' : '#ffffff',
                  transition: 'color 0.3s ease',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                SimplyPayroll
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <>
                <Box
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 4,
                  }}
                >
                  {navItems.map((item) => (
                    <Button
                      key={item.label}
                      component={Link}
                      to={item.path}
                      sx={{
                        color: showSolidBg ? '#1a1a1a' : '#ffffff',
                        fontWeight: 500,
                        '&:hover': {
                          bgcolor: showSolidBg
                            ? 'rgba(0,0,0,0.04)'
                            : 'rgba(255,255,255,0.1)',
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  ))}
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    onClick={() => {
                      window.location.href = `${
                        process.env.REACT_APP_APP_URL ||
                        'https://localhost:6001'
                      }/login`;
                    }}
                    sx={{
                      color: showSolidBg ? '#1a1a1a' : '#ffffff',
                      fontWeight: 500,
                    }}
                  >
                    Sign In
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      window.location.href = `${
                        process.env.REACT_APP_APP_URL || 'https://localhost:6001'
                      }/register`;
                    }}
                    sx={{
                      bgcolor: showSolidBg ? undefined : '#ffffff',
                      color: showSolidBg ? '#ffffff' : '#1E23FE',
                      '&:hover': {
                        bgcolor: showSolidBg
                          ? undefined
                          : 'rgba(255,255,255,0.9)',
                      },
                    }}
                  >
                    Get Started
                  </Button>
                </Box>
              </>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                edge="end"
                onClick={handleDrawerToggle}
                sx={{ color: showSolidBg ? '#1a1a1a' : '#ffffff' }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': { width: 280 },
        }}
      >
        {drawer}
      </Drawer>

      {/* Toolbar spacer - only for non-landing pages */}
      {!isLandingPage && <Toolbar />}
    </>
  );
}
